import { useUser } from "components/clerk";
import Container from "components/general/Container";
import PageHeader from "components/general/Headers/PageHeader";
import ScrollContainer from "components/general/ScrollContainer";
import Section from "components/general/Section";
import { Accordion, AccordionContent, AccordionHeader, AccordionItem, AccordionTrigger, Box, HStack, Icon, Text, VStack } from "components/ui";
import SwitchBasic from "components/ui/SwitchBasic";
import { H4, H6 } from "components/ui/Typography";
import { Bell, ChevronDown, ChevronRight, Mail, MessageSquare, Truck } from "lucide-react-native";
import React, { useCallback, useMemo } from "react";
import { ActivityIndicator } from "react-native";
import { NotificationType, UserNotification, useGetUserNotificationByIdQuery, usePutUserNotificationByIdMutation } from "store/abcoApi";

const orderNotificationSettings = [{ field: "orderConfirmationNotification", label: "Order Confirmation" }];

const transactionNotificationSettings = [{ field: "invoiceCreatedNotification", label: "Invoice" }];

const approvalNotificationSettings = [
	{ field: "cartAwaitingForApprovalNotification", label: "Waiting For Approval" },
	{ field: "cartApprovedNotification", label: "Approved" },
	{ field: "cartRejectedNotification", label: "Rejected" },
];

const shippingNotificationSettings = [
	{ field: "shippingInProgressNotification", label: "In Progress" },
	{ field: "shippingInTransitNotification", label: "In Transit" },
	{ field: "shippingCompletedNotification", label: "Completed" },
	{ field: "shippingFailureNotification", label: "Failure" },
];

export default function Page() {
	const { user } = useUser();
	const { data: userNotification, isLoading } = useGetUserNotificationByIdQuery({ id: Number(user?.externalId) });
	const [putUserNotification] = usePutUserNotificationByIdMutation();

	const approvalNotificationItems = useMemo(
		() =>
			approvalNotificationSettings.map((setting) => ({
				key: setting.field,
				label: setting.label,
			})),
		[],
	);

	const shippingNotificationItems = useMemo(
		() =>
			shippingNotificationSettings.map((setting) => ({
				key: setting.field,
				label: setting.label,
			})),
		[],
	);

	const orderNotificationItems = useMemo(
		() =>
			orderNotificationSettings.map((setting) => ({
				key: setting.field,
				label: setting.label,
			})),
		[],
	);

	const transactionNotificationItems = useMemo(
		() =>
			transactionNotificationSettings.map((setting) => ({
				key: setting.field,
				label: setting.label,
			})),
		[],
	);

	const handleNotificationChange = useCallback(
		(fieldId: keyof UserNotification, label: keyof NotificationType, isChecked: boolean) => {
			if (!userNotification) return;

			const updatedNotification = {
				...userNotification,
				[fieldId]: {
					...((userNotification[fieldId] as NotificationType) || {}),
					[label]: !isChecked,
				},
			};

			putUserNotification({
				id: Number(user?.externalId),
				userNotification: updatedNotification,
			});
		},
		[putUserNotification, userNotification, user],
	);

	if (isLoading || userNotification == null) {
		return (
			<ScrollContainer>
				<Box minHeight={400} justifyContent="center" alignItems="center" padding={20}>
					<ActivityIndicator size="large" color={"#1A428A"} style={{ transform: [{ scale: 1.5 }] }} />
				</Box>
			</ScrollContainer>
		);
	}

	function NotificationCheckboxField({ fieldId, label, isChecked }: { fieldId: keyof UserNotification; label: keyof NotificationType; isChecked: boolean }) {
		// Disable push and text notifications
		const isDisabled = label === "push" || label === "text";
		if (isDisabled) {
			return null;
		}
		return (
			<HStack gap="$2" alignItems="center">
				<SwitchBasic borderBottomWidth={0} text={label} defaultValue={isChecked} onChange={(newValue) => handleNotificationChange(fieldId, label, !newValue)} disabled={isDisabled} />
			</HStack>
		);
	}

	return (
		<ScrollContainer>
			<Container>
				<PageHeader title="Notification Settings" />
				<Section>
					<Accordion gap={12} type="multiple" defaultValue={["approvals", "orders", "transactions", "shipping"]}>
						<AccordionItem borderWidth={1} borderRadius={12} bg="$basicwhite" value="approvals" borderColor="$neutral50" overflow="hidden">
							<AccordionHeader>
								<AccordionTrigger style={{ paddingHorizontal: 16, paddingVertical: 16 }}>
									{({ isExpanded }) => (
										<HStack padding={0} alignItems="center" justifyContent="space-between" width="100%">
											<VStack gap="$1">
												<HStack gap="$3" alignItems="center">
													<Bell size={24} color="#2B6FBB" />
													<H4>Approvals</H4>
												</HStack>
												<Text color="$neutral600" fontSize="$sm">
													Manage notifications for cart approval status changes
												</Text>
											</VStack>
											<Icon as={isExpanded ? ChevronDown : ChevronRight} color="$primarylight" size="lg" />
										</HStack>
									)}
								</AccordionTrigger>
							</AccordionHeader>
							<AccordionContent>
								<VStack space="md" width="100%">
									{approvalNotificationItems.map((item) => (
										<VStack key={item.key} space="sm">
											<H6 style={{ marginBottom: 12, color: "$neutral700" }}>{item.label}</H6>
											<HStack gap={30} flexWrap="wrap">
												{Object.keys((userNotification[item.key as keyof UserNotification] as NotificationType) || {}).map((method) => (
													<NotificationCheckboxField
														key={`${item.key}${method}`}
														fieldId={item.key as keyof UserNotification}
														label={method as keyof NotificationType}
														isChecked={Boolean((userNotification[item.key as keyof UserNotification] as NotificationType)?.[method as keyof NotificationType])}
													/>
												))}
											</HStack>
										</VStack>
									))}
								</VStack>
							</AccordionContent>
						</AccordionItem>
						<AccordionItem borderWidth={1} borderRadius={12} bg="$basicwhite" value="orders" borderColor="$neutral50" overflow="hidden">
							<AccordionHeader>
								<AccordionTrigger style={{ paddingHorizontal: 16, paddingVertical: 16 }}>
									{({ isExpanded }) => (
										<HStack padding={0} alignItems="center" justifyContent="space-between" width="100%">
											<VStack gap="$1">
												<HStack gap="$3" alignItems="center">
													<Mail size={24} color="#2B6FBB" />
													<H4>Orders</H4>
												</HStack>
												<Text color="$neutral600" fontSize="$sm">
													Configure notifications for order-related updates
												</Text>
											</VStack>
											<Icon as={isExpanded ? ChevronDown : ChevronRight} color="$primarylight" size="lg" />
										</HStack>
									)}
								</AccordionTrigger>
							</AccordionHeader>
							<AccordionContent>
								<VStack space="md" width="100%">
									{orderNotificationItems.map((item) => (
										<VStack key={item.key} space="sm">
											<H6 style={{ marginBottom: 12, color: "$neutral700" }}>{item.label}</H6>
											<HStack gap={30} flexWrap="wrap">
												{Object.keys((userNotification[item.key as keyof UserNotification] as NotificationType) || {}).map((method) => (
													<NotificationCheckboxField
														key={`${item.key}${method}`}
														fieldId={item.key as keyof UserNotification}
														label={method as keyof NotificationType}
														isChecked={Boolean((userNotification[item.key as keyof UserNotification] as NotificationType)?.[method as keyof NotificationType])}
													/>
												))}
											</HStack>
										</VStack>
									))}
								</VStack>
							</AccordionContent>
						</AccordionItem>

						<AccordionItem borderWidth={1} borderRadius={12} bg="$basicwhite" value="transactions" borderColor="$neutral50" overflow="hidden">
							<AccordionHeader>
								<AccordionTrigger style={{ paddingHorizontal: 16, paddingVertical: 16 }}>
									{({ isExpanded }) => (
										<HStack padding={0} alignItems="center" justifyContent="space-between" width="100%">
											<VStack gap="$1">
												<HStack gap="$3" alignItems="center">
													<MessageSquare size={24} color="#2B6FBB" />
													<H4>Transactions</H4>
												</HStack>
												<Text color="$neutral600" fontSize="$sm">
													Set up notifications for invoice and payment updates
												</Text>
											</VStack>
											<Icon as={isExpanded ? ChevronDown : ChevronRight} color="$primarylight" size="lg" />
										</HStack>
									)}
								</AccordionTrigger>
							</AccordionHeader>
							<AccordionContent>
								<VStack space="md" width="100%">
									{transactionNotificationItems.map((item) => (
										<VStack key={item.key} space="sm">
											<H6 style={{ marginBottom: 12, color: "$neutral700" }}>{item.label}</H6>
											<HStack gap={30} flexWrap="wrap">
												{Object.keys((userNotification[item.key as keyof UserNotification] as NotificationType) || {}).map((method) => (
													<NotificationCheckboxField
														key={`${item.key}${method}`}
														fieldId={item.key as keyof UserNotification}
														label={method as keyof NotificationType}
														isChecked={Boolean((userNotification[item.key as keyof UserNotification] as NotificationType)?.[method as keyof NotificationType])}
													/>
												))}
											</HStack>
										</VStack>
									))}
								</VStack>
							</AccordionContent>
						</AccordionItem>

						<AccordionItem borderWidth={1} borderRadius={12} bg="$basicwhite" value="shipping" borderColor="$neutral50" overflow="hidden">
							<AccordionHeader>
								<AccordionTrigger style={{ paddingHorizontal: 16, paddingVertical: 16 }}>
									{({ isExpanded }) => (
										<HStack padding={0} alignItems="center" justifyContent="space-between" width="100%">
											<VStack gap="$1">
												<HStack gap="$3" alignItems="center">
													<Truck size={24} color="#2B6FBB" />
													<H4>Shipping</H4>
												</HStack>
												<Text color="$neutral600" fontSize="$sm">
													Control notifications for shipping status updates
												</Text>
											</VStack>
											<Icon as={isExpanded ? ChevronDown : ChevronRight} color="$primarylight" size="lg" />
										</HStack>
									)}
								</AccordionTrigger>
							</AccordionHeader>
							<AccordionContent>
								<VStack space="md" width="100%">
									{shippingNotificationItems.map((item) => (
										<VStack key={item.key} space="sm">
											<H6 style={{ marginBottom: 12, color: "$neutral700" }}>{item.label}</H6>
											<HStack gap={30} flexWrap="wrap">
												{Object.keys((userNotification[item.key as keyof UserNotification] as NotificationType) || {}).map((method) => (
													<NotificationCheckboxField
														key={`${item.key}${method}`}
														fieldId={item.key as keyof UserNotification}
														label={method as keyof NotificationType}
														isChecked={Boolean((userNotification[item.key as keyof UserNotification] as NotificationType)?.[method as keyof NotificationType])}
													/>
												))}
											</HStack>
										</VStack>
									))}
								</VStack>
							</AccordionContent>
						</AccordionItem>
					</Accordion>
				</Section>
			</Container>
		</ScrollContainer>
	);
}
